import type { RouteLocationNormalized } from 'vue-router';

import { isSignedIn, useAuthStore } from '@/stores/auth';
import { CombinedGraphQLErrors } from '@apollo/client';
import { useErrorStore } from '@ohmybible/core';
import { isGranted } from '@pcg/auth';
import { getFirstNestJsGraphQLError } from '@pcg/graphql-kit';
import type { Permission } from '@ohmybible/permissions';

const isPublicPage = (route: RouteLocationNormalized): boolean => Boolean(route.meta.public);

/**
 * Auth middleware for cookie-based authentication.
 * With HTTP-only cookies, we can't check token presence/expiration from JS.
 * Instead, we attempt to fetch user and handle 401 errors via Apollo error link.
 */
export const authMiddleware = async (to: RouteLocationNormalized) => {
  const auth = useAuthStore();

  // === PUBLIC PAGES ===
  if (isPublicPage(to)) {
    console.log(`[middlewares/auth] Public page "${String(to.name)}", skipping auth middleware`);

    // A user known from before this navigation may be stale; one fetched just
    // below is not.
    const userWasKnown = Boolean(auth.user);

    if (!auth.user) {
      try {
        console.log('[middlewares/auth] Fetching user data on public page');
        await auth.fetchMe();
      } catch (e) {
        console.error('[middlewares/auth] Error fetching user on public page:', e);
      }
    }

    // Redirect authenticated users away from the login page — once the
    // server agrees: a tab sent here because its session was revoked still
    // holds the user it had.
    if (isSignedIn(auth.user) && to.name === 'Login') {
      if (userWasKnown) {
        try {
          await auth.syncSession();
        } catch (e) {
          console.error('[middlewares/auth] Error re-reading the session before /login:', e);
        }
      }

      if (isSignedIn(auth.user)) {
        return { name: 'Users' };
      }
    }

    return;
  }

  // === FETCH USER IF NEEDED ===
  if (!auth.user) {
    console.log('[middlewares/auth] Fetching user data');

    try {
      await auth.fetchMe();
    } catch (e) {
      console.error('[middlewares/auth] Error fetching user:', e);

      if (CombinedGraphQLErrors.is(e)) {
        const nestJsError = getFirstNestJsGraphQLError(e);
        const statusCode = nestJsError?.extensions?.statusCode;

        // For 403, 500 or unknown errors — show UErrorView without redirect
        if (statusCode === 403 || statusCode === 500 || !statusCode) {
          const errorStore = useErrorStore();
          errorStore.setError({
            statusCode: statusCode ?? 500,
            title: statusCode === 403 ? 'Forbidden' : 'Error',
            description: nestJsError?.message ?? '',
            critical: true,
          });

          return;
        }
      }

      return {
        name: 'Login',
        query: { redirect_uri: to.fullPath },
      };
    }
  }

  // === CHECK AUTH ===
  if (!isSignedIn(auth.user) && to.name !== 'Login') {
    console.log('[middlewares/auth] No user or guest, redirecting to login');
    return {
      name: 'Login',
      query: { redirect_uri: to.fullPath },
    };
  }

  // === CHECK ADMIN ACCESS ===
  // The admin is a staff tool: a signed-in user without the admin grant (a
  // reader of the app) gets the access-denied page instead of the shell.
  if (auth.user && !isGranted(auth.user, 'omb:core:admin:access' satisfies Permission)) {
    console.log('[middlewares/auth] No admin access, redirecting to access denied');
    return { name: 'AccessDenied' };
  }
};
